import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { UserContext } from "../../utils/contexts/UserContext";
import useDebouncedValue from "../../utils/hooks/useDebouncedValue";
import { ROLE_CODES } from "../../utils/permissions";
import { MAX_PAGE_SIZE } from "../../utils/constants/constants";
import { Field, inputClass } from "../../components/forms/Field";
import FilterSelect from "../../components/forms/FilterSelect";
import MultiSelect from "../../components/forms/MultiSelect";
import PanelHeader from "../../components/timetable/PanelHeader";
import {
  BTN,
  Card,
  EmptyState,
  ErrorState,
  Panel,
  PanelToolbar,
  Refreshing,
  STALE,
  SearchInput,
  TABLE_HEAD,
  TableSkeleton,
} from "../../components/timetable/PanelKit";
import ModalShell from "../Templates/ModalShell";
import ConfirmModal from "../Templates/ConfirmModal";
import { runErrorMessage } from "../../components/timetable/runErrors";
import { clampPageOnEmpty } from "../../utils/helpers/pagination";
import { getGroups } from "../../services/groupService";
import { getDomainSubjects } from "../../services/organizationService";
import { getUsers } from "../../services/userService";
import {
  createActivity,
  deleteActivity,
  getAcademicTerms,
  getActivities,
  getRooms,
  getTimetableSettings,
  updateActivity,
} from "../../services/schedulingService";

const PAGE_SIZE = 20;

// Both the paginated envelope and the flat-array endpoints feed these pickers.
const rowsOf = (data) => (Array.isArray(data) ? data : data?.results || []);

const personName = (person) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(" ").trim() ||
  person?.email ||
  person?.username ||
  "";


export default function ActivitiesPage() {
  const { t } = useTranslation();
  const { user } = useContext(UserContext);

  // Writes are own-school only (the backend derives an activity's school from
  // its class), so every picker is scoped to the caller's own school.
  const orgUuid = user?.organizations?.[0]?.organization?.uuid;

  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  // Both are server-side (`ActivityFilter` carries `term` and a `teacher` M2M
  // lookup) — this list is paginated, so filtering it in the browser would only
  // ever filter the twenty rows of the page you happen to be on.
  //
  // The curriculum is unique per (term, class, subject) and the generator solves
  // ONE term, so a school with a year of curriculum authored was reading four
  // terms interleaved in one list, with the term column the only thing telling
  // them apart and no way to isolate the term they were about to generate.
  const [termFilter, setTermFilter] = useState("");
  const [teacherFilter, setTeacherFilter] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);

  const [groups, setGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [terms, setTerms] = useState([]);
  const [settings, setSettings] = useState(null);

  const [editing, setEditing] = useState(null); // activity | "new" | null
  const [deleting, setDeleting] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reference data: classes, subjects, teachers and the school week. Loaded
  // once — none of it depends on the list query. The week feeds the client-side
  // capacity/split checks so the operator sees the rule, not a 400.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;
    const swallow = (err) => {
      if (err?.code === "ERR_CANCELED") return [];
      return [];
    };

    getGroups({ params: { organization: orgUuid, page_size: MAX_PAGE_SIZE }, signal })
      .then((data) => setGroups(rowsOf(data)))
      .catch(swallow);
    getDomainSubjects({ signal })
      .then((data) => setSubjects(rowsOf(data)))
      .catch(swallow);
    getUsers({
      params: { role: ROLE_CODES.TEACHER, page_size: MAX_PAGE_SIZE },
      signal,
    })
      .then((data) => setTeachers(rowsOf(data)))
      .catch(swallow);
    // Most schools have NO rooms — that is the designed default, and an empty
    // list is what collapses the room controls in the form below.
    getRooms({ params: { organization: orgUuid, page_size: MAX_PAGE_SIZE }, signal })
      .then((data) => setRooms(rowsOf(data)))
      .catch(swallow);
    getTimetableSettings({ signal })
      .then((data) => setSettings(data))
      .catch((err) => {
        if (err?.code === "ERR_CANCELED") return;
        setSettings(null);
      });
    // Unpaginated and already the Generate tab's source for the same list, so
    // the two surfaces name the terms identically.
    getAcademicTerms({ signal })
      .then((data) => setTerms(Array.isArray(data) ? data : rowsOf(data)))
      .catch(swallow);

    return () => controller.abort();
  }, [orgUuid]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, groupFilter, subjectFilter, termFilter, teacherFilter]);

  const loadActivities = useCallback(
    (signal) => {
      setIsLoading(true);
      getActivities({
        params: {
          // Reads are subtree-scoped server-side, but this is a write surface
          // for ONE school: pinning the list to the caller's own school keeps it
          // consistent with the class/subject pickers below it (and with the
          // fact that a row from another school would 403 on save).
          organization: orgUuid || undefined,
          search: debouncedSearch || undefined,
          group: groupFilter || undefined,
          subject: subjectFilter || undefined,
          term: termFilter || undefined,
          teacher: teacherFilter || undefined,
          page,
        },
        signal,
      })
        .then((data) => {
          const results = data?.results || [];
          const count = data?.count ?? 0;
          if (clampPageOnEmpty({ results, count, page, setPage })) return;
          setActivities(results);
          setTotal(count);
          setLoadError(false);
        })
        .catch((err) => {
          if (err?.code === "ERR_CANCELED") return;
          // No toast: a failed list read renders as the panel below (first load)
          // or the inline strip above the table (refetch) — a toast on top of
          // either just repeats it.
          setLoadError(true);
        })
        .finally(() => {
          if (!signal?.aborted) setIsLoading(false);
        });
    },
    [orgUuid, debouncedSearch, groupFilter, subjectFilter, termFilter, teacherFilter, page],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadActivities(controller.signal);
    return () => controller.abort();
  }, [loadActivities]);

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await deleteActivity({ uuid: deleting.uuid });
      toast.success(t("timetable.activities.deleted"));
      setDeleting(null);
      loadActivities();
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.activities.deleteFailed")));
    } finally {
      setIsDeleting(false);
    }
  };

  const groupOptions = useMemo(
    () =>
      groups.map((g) => ({ value: g.uuid, label: g.code ? `${g.code} — ${g.name}` : g.name })),
    [groups],
  );
  const subjectOptions = useMemo(
    () => subjects.map((s) => ({ value: s.uuid, label: s.name })),
    [subjects],
  );
  // /auth/users/ scopes to the caller's whole org SUBTREE, so a head-office
  // manager was being offered teachers from other schools — and the backend
  // rejects those, because an activity's teachers must be staffed in its
  // school. Filter to the pinned school here.
  //
  // Caveat: `organization` on the user list is the PRIMARY active staff link
  // only, so a teacher who also staffs this school as a secondary posting is
  // not offered. Narrowing an over-broad list beats offering choices that 400
  // on save; widening it properly needs an `organization` filter on the users
  // endpoint.
  const teacherOptions = useMemo(
    () =>
      teachers
        .filter((p) => !orgUuid || p.organization?.uuid === orgUuid)
        .map((p) => ({ value: p.uuid, label: personName(p) })),
    [teachers, orgUuid],
  );

  // The current term is marked rather than pre-selected: pre-selecting it would
  // hide curriculum a school had already authored the moment this filter
  // shipped, which is the one thing a filter must never do on arrival.
  const termOptions = useMemo(
    () =>
      terms.map((term) => ({
        value: term.uuid,
        label: term.is_current
          ? t("timetable.activities.termCurrent", { name: term.name })
          : term.name,
      })),
    [terms, t],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const isFiltered = Boolean(
    debouncedSearch || groupFilter || subjectFilter || termFilter || teacherFilter,
  );
  const clearFilters = useCallback(() => {
    setSearch("");
    setGroupFilter("");
    setSubjectFilter("");
    setTermFilter("");
    setTeacherFilter("");
  }, []);
  // The list is paginated, so a refetch keeps the previous page's rows on
  // screen. Without this the only feedback for changing a filter is that
  // nothing happens for as long as the request takes.
  const isRefreshing = isLoading && activities.length > 0;
  const periodsPerDay = settings?.periods_per_day || 0;
  const weeklyCapacity = (settings?.teaching_weekdays?.length || 0) * periodsPerDay;

  // Page wrapper, back link, WIDTH and permission guard live on
  // TimetableWorkspace; rhythm and surfaces come from PanelKit.
  return (
    <>
      <Panel>
        <PanelHeader
          title={t("timetable.activities.heading")}
          count={total}
          subtitle={t("timetable.activities.intro")}
        >
          <button
            type="button"
            onClick={() => setEditing("new")}
            className={BTN.primary}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            {t("timetable.activities.add")}
          </button>
        </PanelHeader>

        {/* Term first, because it scopes everything after it — a curriculum
            line only means anything within one — then class, subject, teacher.
            The long ones turn SEARCHABLE past eight options: a school has 30
            classes, ~20 subjects and 45 teachers, and scrolling a menu to find
            "9B" when you already know it is called 9B is the slowest possible
            way to answer a question you have already answered. */}
        <PanelToolbar>
          <SearchInput
            value={search}
            onChange={setSearch}
            ariaLabel={t("timetable.activities.searchAria")}
          />
          {termOptions.length > 1 && (
            <FilterSelect
              options={[
                { value: "", label: t("timetable.activities.allTerms") },
                ...termOptions,
              ]}
              value={termFilter}
              onChange={setTermFilter}
              ariaLabel={t("timetable.activities.filterTerm")}
              placeholder={t("timetable.activities.allTerms")}
              minWidthClass="min-w-[11rem]"
            />
          )}
          <FilterSelect
            options={[
              { value: "", label: t("timetable.activities.allClasses") },
              ...groupOptions,
            ]}
            value={groupFilter}
            onChange={setGroupFilter}
            ariaLabel={t("timetable.activities.filterClass")}
            placeholder={t("timetable.activities.allClasses")}
            minWidthClass="min-w-[14rem]"
            searchable={groupOptions.length > 8}
            searchPlaceholder={t("common.search")}
            emptyText={t("timetable.activities.noMatches")}
          />
          <FilterSelect
            options={[
              { value: "", label: t("timetable.activities.allSubjects") },
              ...subjectOptions,
            ]}
            value={subjectFilter}
            onChange={setSubjectFilter}
            ariaLabel={t("timetable.activities.filterSubject")}
            placeholder={t("timetable.activities.allSubjects")}
            minWidthClass="min-w-[14rem]"
            searchable={subjectOptions.length > 8}
            searchPlaceholder={t("common.search")}
            emptyText={t("timetable.activities.noMatches")}
          />
          {/* "What does this person teach?" — the question `ActivityFilter`'s
              teacher lookup exists to answer, and the one a manager asks before
              adding to someone's load. */}
          <FilterSelect
            options={[
              { value: "", label: t("timetable.activities.allTeachers") },
              ...teacherOptions,
            ]}
            value={teacherFilter}
            onChange={setTeacherFilter}
            ariaLabel={t("timetable.activities.filterTeacher")}
            placeholder={t("timetable.activities.allTeachers")}
            minWidthClass="min-w-[14rem]"
            searchable={teacherOptions.length > 8}
            searchPlaceholder={t("common.search")}
            emptyText={t("timetable.form.teachersEmpty")}
          />
          {isFiltered && (
            <button type="button" onClick={clearFilters} className={BTN.quiet}>
              {t("common.clearFilters")}
            </button>
          )}
          {isRefreshing && (
            <span className="ml-auto">
              <Refreshing />
            </span>
          )}
        </PanelToolbar>

        {isLoading && activities.length === 0 ? (
          <TableSkeleton
            rows={6}
            columns={[
              "w-16",
              "w-32",
              "w-40 hidden md:block",
              "w-8",
              "w-24 hidden lg:block",
            ]}
          />
        ) : loadError && activities.length === 0 ? (
          <ErrorState
            title={t("timetable.activities.loadFailed")}
            onRetry={() => loadActivities()}
          />
        ) : activities.length === 0 ? (
          <ActivitiesEmptyState
            isFiltered={isFiltered}
            onAdd={() => setEditing("new")}
            onClearFilters={clearFilters}
          />
        ) : (
          <Card padded={false}>
            {loadError && (
              <div
                role="alert"
                className="px-6 py-2.5 bg-rose-50 text-rose-700 text-[11px] font-black border-b border-rose-100"
              >
                {t("timetable.activities.refreshFailed")}
              </div>
            )}
            <div className={`overflow-x-auto ${isRefreshing ? STALE : ""}`}>
              <table className="w-full text-sm" aria-busy={isLoading}>
                <thead>
                  <tr className={TABLE_HEAD}>
                    <th className="px-6 py-3">{t("timetable.activities.colClass")}</th>
                    <th className="px-6 py-3">{t("timetable.activities.colSubject")}</th>
                    <th className="px-6 py-3 hidden md:table-cell">
                      {t("timetable.activities.colTeachers")}
                    </th>
                    <th className="px-6 py-3">{t("timetable.activities.colPeriods")}</th>
                    <th className="px-6 py-3 hidden lg:table-cell">
                      {t("timetable.activities.colBlocks")}
                    </th>
                    <th className="px-6 py-3 hidden lg:table-cell">
                      {t("timetable.activities.colTerm")}
                    </th>
                    <th className="px-6 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {activities.map((activity) => (
                    <ActivityRow
                      key={activity.uuid}
                      activity={activity}
                      onEdit={() => setEditing(activity)}
                      onDelete={() => setDeleting(activity)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500">
              {t("timetable.activities.pageOf", { page, total: totalPages })}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || isLoading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={BTN.secondary}
              >
                {t("timetable.activities.prev")}
              </button>
              <button
                type="button"
                disabled={page >= totalPages || isLoading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className={BTN.secondary}
              >
                {t("timetable.activities.next")}
              </button>
            </div>
          </div>
        )}
      </Panel>

      {editing && (
        <ActivityFormModal
          activity={editing === "new" ? null : editing}
          groupOptions={groupOptions}
          groupRows={groups}
          subjectOptions={subjectOptions}
          teacherOptions={teacherOptions}
          rooms={rooms}
          periodsPerDay={periodsPerDay}
          weeklyCapacity={weeklyCapacity}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadActivities();
          }}
        />
      )}

      {deleting && (
        <ConfirmModal
          title={t("timetable.activities.deleteTitle")}
          body={t("timetable.activities.deleteBody", {
            group: deleting.group?.code || deleting.group?.name || "",
            subject: deleting.subject?.name || "",
          })}
          confirmLabel={
            isDeleting ? t("timetable.activities.deleting") : t("timetable.activities.delete")
          }
          danger
          busy={isDeleting}
          onConfirm={confirmDelete}
          onClose={() => setDeleting(null)}
        />
      )}
    </>
  );
}

// -- table row ---------------------------------------------------------------

function ActivityRow({ activity, onEdit, onDelete }) {
  const { t } = useTranslation();
  const durations = activity.card_durations || [];
  const hasDoubles = durations.some((d) => d > 1);

  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50">
      <td className="px-6 py-3 font-medium text-slate-900">
        {activity.group?.code || activity.group?.name || "—"}
      </td>
      <td className="px-6 py-3 text-slate-700">{activity.subject?.name || "—"}</td>
      <td className="px-6 py-3 text-slate-600 hidden md:table-cell">
        <div className="flex flex-wrap gap-1">
          {(activity.teachers || []).map((teacher) => (
            <span
              key={teacher.uuid}
              className="inline-block px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 text-[11px] font-semibold"
            >
              {personName(teacher)}
            </span>
          ))}
        </div>
      </td>
      <td className="px-6 py-3 font-semibold text-slate-900">{activity.periods_per_week}</td>
      <td className="px-6 py-3 hidden lg:table-cell">
        {hasDoubles ? (
          <span className="inline-flex items-center gap-1">
            {durations.map((d, i) => (
              <span
                key={i}
                className={`inline-block px-2 py-0.5 rounded-md text-[11px] font-bold ${
                  d > 1 ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-500"
                }`}
              >
                {d}
              </span>
            ))}
          </span>
        ) : (
          <span className="text-[11px] text-slate-500">
            {t("timetable.activities.allSingles")}
          </span>
        )}
      </td>
      <td className="px-6 py-3 text-slate-500 hidden lg:table-cell">
        {activity.term_name || "—"}
      </td>
      <td className="px-6 py-3">
        <div className="flex items-center justify-end gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="px-2 py-1 rounded-lg text-xs font-semibold text-indigo-600 hover:bg-indigo-50 transition"
          >
            {t("timetable.activities.edit")}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="px-2 py-1 rounded-lg text-xs font-semibold text-rose-600 hover:bg-rose-50 transition"
          >
            {t("timetable.activities.delete")}
          </button>
        </div>
      </td>
    </tr>
  );
}

// -- empty / loading ---------------------------------------------------------

// A school starts with zero activities and this page is the entry point to the
// whole generator, so the empty state has to teach, not just report.
function ActivitiesEmptyState({ isFiltered, onAdd, onClearFilters }) {
  const { t } = useTranslation();
  return (
    <EmptyState
      iconPath={
        isFiltered
          ? "M21 21l-4.3-4.3m1.8-4.7a6.5 6.5 0 11-13 0 6.5 6.5 0 0113 0z"
          : "M4 6h16M4 12h16M4 18h10"
      }
      title={
        isFiltered ? t("timetable.activities.noMatches") : t("timetable.activities.empty")
      }
      hint={
        isFiltered
          ? t("timetable.activities.noMatchesHint")
          : t("timetable.activities.emptyHint")
      }
      action={
        // A dead end otherwise: four filters can combine into an empty result
        // and the way back is to remember which of them you set.
        isFiltered ? (
          <button type="button" onClick={onClearFilters} className={BTN.secondary}>
            {t("common.clearFilters")}
          </button>
        ) : (
          <button type="button" onClick={onAdd} className={BTN.primary}>
            {t("timetable.activities.emptyAction")}
          </button>
        )
      }
    />
  );
}

// -- create / edit modal -----------------------------------------------------

// A room reference can arrive as a nested object or a bare uuid depending on the
// serializer; both mean the same thing here.
const roomUuidOf = (room) => (typeof room === "string" ? room : room?.uuid);

function ActivityFormModal({
  groupRows,
  activity,
  groupOptions,
  subjectOptions,
  teacherOptions,
  rooms,
  periodsPerDay,
  weeklyCapacity,
  onClose,
  onSaved,
}) {
  const { t } = useTranslation();
  const isEdit = Boolean(activity);

  const [group, setGroup] = useState(activity?.group?.uuid || "");
  const [subject, setSubject] = useState(activity?.subject?.uuid || "");
  const [teachers, setTeachers] = useState(
    (activity?.teachers || []).map((teacher) => teacher.uuid),
  );
  const [periodsPerWeek, setPeriodsPerWeek] = useState(
    activity ? String(activity.periods_per_week) : "",
  );
  // How this subject distributes across the week. Defaults to EVEN for a new
  // line, matching the model — a school assumes its subjects are spread without
  // ever thinking to ask for it, and before this existed nothing enforced it.
  const [spread, setSpread] = useState(activity?.spread || "even");
  // `split` is the double-period control. An empty split means "every period is
  // its own single lesson" — that is the backend default, not a missing value.
  const [split, setSplit] = useState([...(activity?.split || [])]);
  const [notes, setNotes] = useState(activity?.notes || "");
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // -- where the lesson can be held (aSc's four ticks + an explicit list) -----
  // A NEW lesson starts on the class's own room, matching the backend default.
  const [useHomeRoom, setUseHomeRoom] = useState(
    activity ? Boolean(activity.use_home_room) : true,
  );
  const [useTeacherRoom, setUseTeacherRoom] = useState(Boolean(activity?.use_teacher_room));
  const [useSharedRooms, setUseSharedRooms] = useState(Boolean(activity?.use_shared_rooms));
  const [useSubjectRooms, setUseSubjectRooms] = useState(Boolean(activity?.use_subject_rooms));
  const [namedRooms, setNamedRooms] = useState(
    (activity?.rooms || []).map(roomUuidOf).filter(Boolean),
  );

  const roomOptions = useMemo(
    () =>
      (rooms || []).map((room) => ({
        value: room.uuid,
        label: room.code ? `${room.code} — ${room.name}` : room.name,
      })),
    [rooms],
  );

  // The union, shown back rather than left to be guessed. Shared/subject/named
  // rooms resolve to real names here; the class's and the teacher's own rooms
  // live on the class and staff records, so they are named descriptively.
  const roomPool = useMemo(() => {
    const labels = [];
    const seen = new Set();
    const pushRoom = (room) => {
      if (!room || seen.has(room.uuid)) return;
      seen.add(room.uuid);
      labels.push(room.code || room.name);
    };

    // The class's own room is REAL data — GroupSerializer exposes
    // `home_room_detail` — so resolve it rather than asserting a label. The
    // previous version pushed "the class's own room" unconditionally, which
    // told the user a lesson could be held somewhere it could not: most classes
    // have no `home_room` at all, and the backend's pool would come back empty.
    if (useHomeRoom) {
      const cls = (groupRows || []).find((g) => g.uuid === group);
      pushRoom(cls?.home_room_detail);
    }
    // The teacher's room is NOT exposed to this page (it lives on the staff
    // link), so it is shown as a conditional note, never as a resolved room.
    // The authoritative pool is `resolved_room_pool` on the saved activity,
    // computed by the same rule the solver uses.
    if (useTeacherRoom) labels.push(t("timetable.form.poolTeacherRoom"));
    if (useSharedRooms) (rooms || []).filter((r) => r.is_shared).forEach(pushRoom);
    if (useSubjectRooms && subject) {
      (rooms || [])
        .filter((r) => (r.subjects || []).map(roomUuidOf).includes(subject))
        .forEach(pushRoom);
    }
    namedRooms.forEach((uuid) => pushRoom((rooms || []).find((r) => r.uuid === uuid)));
    return labels;
  }, [t, rooms, groupRows, group, subject, useHomeRoom, useTeacherRoom,
      useSharedRooms, useSubjectRooms, namedRooms]);

  const nothingTicked =
    !useHomeRoom &&
    !useTeacherRoom &&
    !useSharedRooms &&
    !useSubjectRooms &&
    namedRooms.length === 0;

  const total = Number(periodsPerWeek);
  const splitSum = split.reduce((sum, n) => sum + n, 0);
  const maxBlock = periodsPerDay || 8;

  const validate = () => {
    const next = {};
    if (!group) next.group = t("timetable.form.errClass");
    if (!subject) next.subject = t("timetable.form.errSubject");
    if (teachers.length === 0) next.teachers = t("timetable.form.errTeachers");
    if (!Number.isInteger(total) || total < 1) {
      next.periods_per_week = t("timetable.form.errPeriods");
    } else if (weeklyCapacity > 0 && total > weeklyCapacity) {
      next.periods_per_week = t("timetable.form.errCapacity", { capacity: weeklyCapacity });
    }
    if (split.length > 0) {
      if (split.some((n) => n > maxBlock)) {
        next.split = t("timetable.form.errBlockTooLong", { max: maxBlock });
      } else if (splitSum !== total) {
        next.split = t("timetable.form.errBlockSum", { sum: splitSum, total: total || 0 });
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSaving(true);
    try {
      const body = {
        group,
        subject,
        teachers,
        periods_per_week: total,
        split,
        spread,
        notes,
        use_home_room: useHomeRoom,
        use_teacher_room: useTeacherRoom,
        use_shared_rooms: useSharedRooms,
        use_subject_rooms: useSubjectRooms,
        rooms: namedRooms,
      };
      if (isEdit) {
        await updateActivity({ uuid: activity.uuid, body });
        toast.success(t("timetable.activities.updated"));
      } else {
        await createActivity({ body });
        toast.success(t("timetable.activities.created"));
      }
      onSaved();
    } catch (err) {
      toast.error(runErrorMessage(err, t, t("timetable.activities.saveFailed")));
    } finally {
      setIsSaving(false);
    }
  };

  const addBlock = () => {
    // The first block a person adds is almost always the double they came for.
    setSplit((prev) => [...prev, prev.length === 0 ? Math.min(2, maxBlock) : 1]);
  };
  const patchBlock = (index, delta) => {
    setSplit((prev) =>
      prev.map((n, i) => (i === index ? Math.min(maxBlock, Math.max(1, n + delta)) : n)),
    );
  };
  const removeBlock = (index) => setSplit((prev) => prev.filter((_, i) => i !== index));

  // Fill the remaining periods with singles — the fastest path from "I want one
  // double" to a split that actually adds up.
  const fillSingles = () => {
    const remaining = total - splitSum;
    if (!Number.isInteger(remaining) || remaining <= 0) return;
    setSplit((prev) => [...prev, ...Array.from({ length: remaining }, () => 1)]);
  };

  return (
    <ModalShell onClose={onClose} busy={isSaving} maxWidth="max-w-2xl">
      <h3 className="text-lg font-bold text-slate-900 pr-8">
        {isEdit ? t("timetable.form.editTitle") : t("timetable.form.createTitle")}
      </h3>

      <form onSubmit={submit} noValidate className="mt-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label={t("timetable.form.class")} error={errors.group} required>
            <select
              value={group}
              disabled={isSaving}
              onChange={(e) => setGroup(e.target.value)}
              className={inputClass(errors.group)}
            >
              <option value="">{t("timetable.form.classPlaceholder")}</option>
              {groupOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("timetable.form.subject")} error={errors.subject} required>
            <select
              value={subject}
              disabled={isSaving}
              onChange={(e) => setSubject(e.target.value)}
              className={inputClass(errors.subject)}
            >
              <option value="">{t("timetable.form.subjectPlaceholder")}</option>
              {subjectOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label={t("timetable.form.teachers")} error={errors.teachers} required>
          <MultiSelect
            options={teacherOptions}
            value={teachers}
            onChange={setTeachers}
            disabled={isSaving}
            placeholder={t("timetable.form.teachersPlaceholder")}
            emptyText={t("timetable.form.teachersEmpty")}
          />
          <p className="mt-1 text-xs text-slate-400">{t("timetable.form.teachersHint")}</p>
        </Field>

        <Field
          label={t("timetable.form.periods")}
          error={errors.periods_per_week}
          required
        >
          <div className="max-w-[8rem]">
            <input
              type="number"
              min="1"
              step="1"
              value={periodsPerWeek}
              disabled={isSaving}
              onChange={(e) => setPeriodsPerWeek(e.target.value)}
              className={inputClass(errors.periods_per_week)}
            />
          </div>
          <p className="mt-1 text-xs text-slate-400">
            {weeklyCapacity > 0
              ? t("timetable.form.periodsHintCapacity", { capacity: weeklyCapacity })
              : t("timetable.form.periodsHint")}
          </p>
        </Field>

        {/* -- how it spreads across the week ---------------------------------
            Only meaningful once there is more than one lesson to spread. */}
        {total > 1 && (
          <Field label={t("timetable.spread.label")} hint={t("timetable.spread.hint")}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {["even", "loose", "none"].map((option) => (
                <button
                  key={option}
                  type="button"
                  aria-pressed={spread === option}
                  disabled={isSaving}
                  onClick={() => setSpread(option)}
                  className={`px-3 py-2 rounded-xl border text-[11px] font-black uppercase tracking-widest transition disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 ${
                    spread === option
                      ? "border-indigo-500 bg-indigo-50 text-indigo-700 ring-2 ring-indigo-500/20"
                      : "border-slate-200 bg-white text-slate-500 hover:border-indigo-300"
                  }`}
                >
                  {t(`timetable.spread.${option}`)}
                </button>
              ))}
            </div>
          </Field>
        )}

        {/* -- the double-period control -------------------------------------- */}
        <Field label={t("timetable.form.blocks")} error={errors.split}>
          <p className="text-xs text-slate-400 leading-relaxed">
            {t("timetable.form.blocksHint")}
          </p>

          <div className="mt-2 flex flex-wrap items-center gap-2">
            {split.map((duration, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-slate-50 pl-1 pr-1 py-1"
              >
                <BlockButton
                  label={t("timetable.form.blockShorter", { n: i + 1 })}
                  disabled={isSaving || duration <= 1}
                  onClick={() => patchBlock(i, -1)}
                  path="M18 12H6"
                />
                <span className="min-w-[2.5rem] text-center text-sm font-black text-slate-700">
                  {duration}
                </span>
                <BlockButton
                  label={t("timetable.form.blockLonger", { n: i + 1 })}
                  disabled={isSaving || duration >= maxBlock}
                  onClick={() => patchBlock(i, 1)}
                  path="M12 6v12M6 12h12"
                />
                <BlockButton
                  label={t("timetable.form.removeBlock", { n: i + 1 })}
                  disabled={isSaving}
                  danger
                  onClick={() => removeBlock(i)}
                  path="M6 18L18 6M6 6l12 12"
                />
              </span>
            ))}
            <button
              type="button"
              onClick={addBlock}
              disabled={isSaving}
              className="px-3 py-2 rounded-xl border-2 border-dashed border-slate-200 text-xs font-bold text-slate-500 hover:border-indigo-300 hover:text-indigo-600 hover:bg-indigo-50/50 transition disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {t("timetable.form.addBlock")}
            </button>
          </div>

          <div className="mt-2 flex items-center gap-3 flex-wrap">
            {split.length === 0 ? (
              <span className="text-[11px] font-bold text-slate-500">
                {t("timetable.form.singlesOnly")}
              </span>
            ) : (
              <span
                className={`text-[11px] font-black uppercase tracking-widest ${
                  splitSum === total ? "text-emerald-600" : "text-amber-600"
                }`}
              >
                {t("timetable.form.blocksSum", { sum: splitSum, total: total || 0 })}
              </span>
            )}
            {split.length > 0 && total > splitSum && (
              <button
                type="button"
                onClick={fillSingles}
                disabled={isSaving}
                className="text-[11px] font-bold text-indigo-600 hover:text-indigo-700 underline underline-offset-2 disabled:opacity-50"
              >
                {t("timetable.form.fillSingles", { n: total - splitSum })}
              </button>
            )}
          </div>
        </Field>

        {/* -- where the lesson can be held ----------------------------------- */}
        {roomOptions.length === 0 ? (
          // A school with no rooms has nothing to choose between, and four dead
          // checkboxes would be pure noise. Point at the Rooms page instead.
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/60 px-4 py-3">
            <p className="text-xs font-bold text-slate-600">{t("timetable.form.roomsNone")}</p>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">
              {t("timetable.form.roomsNoneHint")}
            </p>
            <Link
              to="/timetable/rooms"
              className="mt-1.5 inline-block text-xs font-bold text-indigo-600 hover:text-indigo-700 underline underline-offset-2"
            >
              {t("timetable.form.roomsNoneLink")}
            </Link>
          </div>
        ) : (
          <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              {t("timetable.form.roomsTitle")}
            </p>
            <p className="mt-1 text-xs text-slate-400 leading-relaxed">
              {t("timetable.form.roomsHint")}
            </p>

            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5">
              <RoomCheck
                label={t("timetable.form.useHomeRoom")}
                hint={t("timetable.form.useHomeRoomHint")}
                checked={useHomeRoom}
                disabled={isSaving}
                onChange={setUseHomeRoom}
              />
              <RoomCheck
                label={t("timetable.form.useTeacherRoom")}
                hint={t("timetable.form.useTeacherRoomHint")}
                checked={useTeacherRoom}
                disabled={isSaving}
                onChange={setUseTeacherRoom}
              />
              <RoomCheck
                label={t("timetable.form.useSharedRooms")}
                hint={t("timetable.form.useSharedRoomsHint")}
                checked={useSharedRooms}
                disabled={isSaving}
                onChange={setUseSharedRooms}
              />
              <RoomCheck
                label={t("timetable.form.useSubjectRooms")}
                hint={t("timetable.form.useSubjectRoomsHint")}
                checked={useSubjectRooms}
                disabled={isSaving}
                onChange={setUseSubjectRooms}
              />
            </div>

            <div className="mt-4">
              <Field label={t("timetable.form.namedRooms")}>
                <MultiSelect
                  options={roomOptions}
                  value={namedRooms}
                  onChange={setNamedRooms}
                  disabled={isSaving}
                  placeholder={t("timetable.form.namedRoomsPlaceholder")}
                  emptyText={t("timetable.form.namedRoomsEmpty")}
                />
                <p className="mt-1 text-xs text-slate-400 leading-relaxed">
                  {t("timetable.form.namedRoomsHint")}
                </p>
              </Field>
            </div>

            {/* The union, spelled out — so nobody has to guess what four ticks
                and a list add up to. */}
            <div className="mt-4 pt-3 border-t border-slate-200">
              {nothingTicked ? (
                <p className="text-xs text-slate-500 leading-relaxed">
                  {t("timetable.form.poolAnywhere")}
                </p>
              ) : roomPool.length === 0 ? (
                <p className="text-xs text-amber-600 font-semibold leading-relaxed">
                  {t("timetable.form.poolUnresolved")}
                </p>
              ) : (
                <p className="text-xs text-slate-600 leading-relaxed">
                  <span className="font-bold text-slate-500">
                    {t("timetable.form.poolLabel")}
                  </span>{" "}
                  {roomPool.join(", ")}
                </p>
              )}
              {(useHomeRoom || useTeacherRoom) && (
                <p className="mt-1.5 text-[11px] text-slate-500 leading-relaxed">
                  {t("timetable.form.poolImplicitHint")}
                </p>
              )}
            </div>
          </div>
        )}

        <Field label={t("timetable.form.notes")}>
          <textarea
            rows={2}
            value={notes}
            disabled={isSaving}
            onChange={(e) => setNotes(e.target.value)}
            className={inputClass(false)}
          />
        </Field>

        <div className="flex justify-end gap-2 pt-3 mt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={onClose}
            disabled={isSaving}
            className={BTN.quiet}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className={BTN.primary}
          >
            {isSaving
              ? t("common.saving")
              : isEdit
                ? t("timetable.form.save")
                : t("timetable.form.create")}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function RoomCheck({ label, hint, checked, disabled, onChange }) {
  return (
    <label className="flex items-start gap-2.5 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500/30"
      />
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-slate-700">{label}</span>
        <span className="block text-xs text-slate-400 leading-relaxed">{hint}</span>
      </span>
    </label>
  );
}

function BlockButton({ label, path, onClick, disabled, danger = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={`p-1 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        danger ? "text-rose-500 hover:bg-rose-50" : "text-slate-400 hover:text-indigo-600 hover:bg-white"
      }`}
    >
      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d={path} />
      </svg>
    </button>
  );
}
